import { useEffect, useRef, useState } from "react";

// Parses "278", "±1.250", "+500", "4" etc → { prefix, target, suffix }
function parseStatValue(raw) {
  const match = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
  if (!match) return { prefix: "", target: 0, suffix: raw, divider: 1 };
  const prefix = match[1] || "";
  const suffix = match[3] || "";
  const numStr = match[2];
  // Determine if comma is thousand separator (BE format)
  const cleaned = numStr.replace(/[.,]/g, "");
  const target = parseInt(cleaned, 10);
  // Detect original formatting: if "1.250" → uses "." as thousand separator
  const useThousandDot = /\d\.\d{3}/.test(numStr);
  return { prefix, target, suffix, useThousandDot };
}

function formatNumber(n, useThousandDot) {
  if (!useThousandDot) return String(n);
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default function CountUp({ value, duration = 1600, className = "" }) {
  const { prefix, target, suffix, useThousandDot } = parseStatValue(value);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setCurrent(Math.round(target * eased));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatNumber(current, useThousandDot)}
      {suffix}
    </span>
  );
}
